import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { handleCustomerRequest } from '../customer-api-client';
import { Cart, CartItem } from './cart';
import { AddCartItemRequest } from './cart';
import { CartParsers } from './cart-parser';
import { CreateOrderRequest } from '../../quipli-service/order/order';
import { OrderParsers } from '../../quipli-service/order/order-parsers';
import { PaymentMethodRequest } from '../../quipli-service/payment/payment';
import { DateTime } from 'luxon';

export const useCustomerCartApi = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function getCart(locationId: number) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/cart`,
      headers: {
        'X-Tenant': tenant,
        'X-Location-Id': locationId,
      },
    };

    const result = await handleCustomerRequest<CartParsers.CartJson | undefined>(config);
    return result && CartParsers.toCart(result);
  }

  async function addItemToCart(locationId: number, req: AddCartItemRequest): Promise<Cart> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/cart/items`,
      headers: {
        'X-Tenant': tenant,
        'X-Location-Id': locationId,
      },
      data: req,
    };
    const json = await handleCustomerRequest<CartParsers.CartJson>(config);
    return CartParsers.toCart(json);
  }

  async function updateCartItem(item: CartItem, skipAvailabilityCheck: boolean = false, locationId: number) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/customer-api/v1/cart/items/${item.id}`,
      headers: {
        'X-Tenant': tenant,
        'X-Location-Id': locationId,
      },
      data: {
        ...item,
        skipAvailabilityCheck,
      },
    };

    const result = await handleCustomerRequest<CartParsers.CartJson>(config);
    return CartParsers.toCart(result);
  }

  async function removeItemFromCart(cartItemId: string, locationId: number) {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/customer-api/v1/cart/items/${cartItemId}`,
      headers: {
        'X-Tenant': tenant,
        'X-Location-Id': locationId,
      },
    };

    const result = await handleCustomerRequest<CartParsers.CartJson>(config);
    return CartParsers.toCart(result);
  }

  async function updateCartMeta(meta: Record<string, any>, locationId: number) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/customer-api/v1/cart`,
      headers: {
        'X-Tenant': tenant,
        'X-Location-Id': locationId,
      },
      data: meta,
    };

    const result = await handleCustomerRequest<CartParsers.CartJson>(config);
    return CartParsers.toCart(result);
  }

  async function placeOrder(request: Omit<CreateOrderRequest, 'customerId'>) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/cart/placeOrder`,
      headers: {
        'X-Tenant': tenant,
        'X-Location-Id': request.locationId,
      },
      data: request,
    };

    const result = await handleCustomerRequest<OrderParsers.OrderJson>(config);
    return OrderParsers.toOrder(result);
  }

  async function acceptQuote(
    orderId: number,
    locationId: number,
    paymentMethod: PaymentMethodRequest,
    billingAddressId?: number,
    deliveryAddressId?: number,
    dateSigned?: DateTime,
  ) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/cart/acceptQuote`,
      headers: {
        'X-Tenant': tenant,
        'X-Location-Id': locationId,
      },
      data: {
        orderId,
        paymentMethod,
        billingAddressId,
        deliveryAddressId,
        dateSigned,
      },
    };

    const result = await handleCustomerRequest<OrderParsers.OrderJson>(config);
    return OrderParsers.toOrder(result);
  }

  return {
    getCart,
    addItemToCart,
    updateCartItem,
    removeItemFromCart,
    updateCartMeta,
    placeOrder,
    acceptQuote,
  };
};
